// src/hooks/useBidVerification.js
// Hook para la lógica de verificación de ofertas desde email
// Extrae toda la lógica del componente VerifyBid para mantenerlo ligero

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getBidIntent, markIntentAsUsed } from '../services/supabase/bidIntents';
import { createEngagementEvent, generateSessionId, getBrowserMetadata } from '../services/supabase/engagement';
import { createBid, getProperty, getPropertyBids, getPropertyInvites, extendDeadlineAntiSniping, upsertBlindOffer, verifyBlindOffer } from '../services/supabaseService';
import { getPropertyStatus } from '../utils/propertyHelpers';
import { ANTI_SNIPING_THRESHOLD, ANTI_SNIPING_EXTENSION, ANTI_SNIPING_MAX_EXTENSION } from '../config/antiSnipingConfig';
import { getSemaphoreThresholds } from '../config/blindBiddingConfig';

/**
 * Hook para gestionar la verificación de ofertas desde email
 * @param {string} token - Token del bid intent
 * @param {string} inviteToken - Token de la invitación
 * @returns {object} Estados y funciones para el componente
 */
export const useBidVerification = (token, inviteToken) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'success' | 'expired' | 'invalid' | 'error'
  const [intent, setIntent] = useState(null);
  const [property, setProperty] = useState(null);
  const [message, setMessage] = useState('');

  // Estados anti-sniping
  const [antiSnipingTriggered, setAntiSnipingTriggered] = useState(false);
  const [newDeadlineTime, setNewDeadlineTime] = useState(null);

  // Ejecutar verificación al montar
  useEffect(() => {
    if (token) {
      verifyAndCreateBid();
    } else {
      setStatus('error');
      setMessage(t('verify.messages.noToken'));
      setLoading(false);
    }
  }, [token]);

  /**
   * Proceso principal de verificación y creación de oferta
   */
  const verifyAndCreateBid = async () => {
    try {
      // 1. Obtener el intent
      const intentData = await getBidIntent(token);
      
      if (!intentData) {
        setStatus('expired');
        setMessage(t('verify.messages.expired'));
        await trackExpiredIntent();
        setLoading(false);
        return;
      }

      setIntent(intentData);

      // 2. Obtener información de la propiedad
      const propertyData = await getProperty(intentData.property_id);
      setProperty(propertyData);

      // 2.5 Validar que la subasta sigue activa
      const currentStatus = getPropertyStatus(propertyData);
      if (currentStatus === 'CLOSED') {
        setStatus('invalid');
        setMessage(t('verify.messages.statusChanged'));
        setLoading(false);
        return;
      }
      if (currentStatus === 'LAST_CHANCE' && intentData.sent_in_status !== 'LAST_CHANCE') {
        setStatus('invalid');
        setMessage(t('verify.messages.statusChanged'));
        setLoading(false);
        return;
      }

      // 3. Detectar tipo de oferta según el estado en que se inició (no el actual)
      const isBlindBid = ['BLIND_WAITING', 'BLIND_ACTIVE', 'UPCOMING_BLIND'].includes(intentData.sent_in_status);
      const isLastChanceBid = intentData.sent_in_status === 'LAST_CHANCE';

      // ========== CASO A: OFERTA CIEGA ==========
      if (isBlindBid) {
        // En fase ciega no hay validación de mínimos competitivos
        // Solo validar que el monto sea positivo
        if (intentData.amount <= 0) {
          setStatus('invalid');
          setMessage(t('verify.messages.invalidAmount'));
          setLoading(false);
          return;
        }

        // Obtener el invite_id desde la base de datos usando el email
        const allInvites = await getPropertyInvites(intentData.property_id);
        const currentInvite = allInvites.find(inv => inv.email === intentData.buyer_email);

        if (!currentInvite) {
          setStatus('invalid');
          setMessage(t('verify.messages.inviteNotFound'));
          setLoading(false);
          return;
        }

        // Obtener umbrales de semáforo de la propiedad
        const thresholds = getSemaphoreThresholds(propertyData);

        // Crear/actualizar oferta ciega
        const blindOfferResult = await upsertBlindOffer({
          propertyId: intentData.property_id,
          inviteId: currentInvite.id,
          buyerName: intentData.buyer_name,
          buyerEmail: intentData.buyer_email,
          amount: intentData.amount,
          conditionsText: intentData.conditions_text || '',
          startPrice: propertyData.startPrice,
          thresholds: thresholds,
        });

        // Marcar como verificada
        await verifyBlindOffer(blindOfferResult.id);

        // ✅ SIMPLIFICADO: Todas las ofertas verificadas requieren activación manual desde admin
        // No se activa automáticamente BLIND_ACTIVE, independientemente del color del semáforo
        if (currentStatus === 'BLIND_WAITING') {
          console.log(`✅ Oferta ciega verificada (${blindOfferResult.semaphoreStatus.toUpperCase()}) - Requiere activación manual desde admin`);
        }

        // Marcar intent como usado
        await markIntentAsUsed(token);

        // Mostrar éxito
        setStatus('success');
        setMessage(t('verify.messages.blindSuccess', { amount: formatAmount(intentData.amount) }));

        // Tracking
        await trackVerifiedBid(intentData, false);

        setLoading(false);
        return;
      }

      // ========== CASO B: OFERTA NORMAL (OPEN / LAST_CHANCE) ==========
      // 3. Obtener ofertas actuales para validar
      const currentBids = await getPropertyBids(intentData.property_id);
      const highestBid = currentBids[0];

      // 4. Validar que el monto del intent sigue siendo válido
      // ✅ Solo en periodo OPEN - en LAST_CHANCE ya se validó en frontend
      if (!isLastChanceBid) {
        const currentMinimum = highestBid 
          ? highestBid.amount + propertyData.minIncrement
          : propertyData.minAbsolute;

        if (intentData.amount < currentMinimum) {
          setStatus('invalid');
          setMessage(t('verify.messages.outbid'));
          setLoading(false);
          return;
        }
      }

      // 5. Todo válido - Crear la oferta automáticamente
      const previousTopBidder = currentBids[0] || null;

      // Asegurar que selected_conditions es un array (la RPC puede devolverlo como JSON string)
      const rawConditions = intentData.selected_conditions;
      const selectedConditionIds = Array.isArray(rawConditions)
        ? rawConditions
        : typeof rawConditions === 'string' && rawConditions
          ? JSON.parse(rawConditions)
          : [];

      const bidResult = await createBid({
        propertyId: intentData.property_id,
        buyerName: intentData.buyer_name,
        buyerEmail: intentData.buyer_email,
        amount: intentData.amount,
        selectedConditionIds,
        conditionsText: intentData.conditions_text || '',
        is_last_chance: isLastChanceBid
      });

      if (!bidResult.success) {
        setStatus('invalid');
        setMessage(bidResult.error || t('verify.messages.genericError'));
        setLoading(false);
        return;
      }

      // 5.5 Anti-sniping: Extender deadline si quedan menos de X minutos
      // Solo aplicar en periodo OPEN (no en LAST_CHANCE)
      if (!isLastChanceBid) {
        await handleAntiSniping(propertyData, intentData);
      }

      // 6. Marcar intent como usado
      await markIntentAsUsed(token);

      // 7. Enviar notificaciones (solo si NO es última oportunidad)
      if (!isLastChanceBid) {
        await sendBidNotifications(previousTopBidder, propertyData, intentData);
      }

      // 8. Mostrar éxito
      setStatus('success');
      setMessage(t('verify.messages.bidSuccess', { amount: formatAmount(intentData.amount) }));

      // 9. Tracking de verificación exitosa
      await trackVerifiedBid(intentData, isLastChanceBid);

      setLoading(false);

    } catch (error) {
      console.error('Error verificando oferta:', error);
      setStatus('error');
      setMessage(t('verify.messages.genericError'));
      setLoading(false);
    }
  };

  /**
   * Maneja la lógica de anti-sniping
   */
  const handleAntiSniping = async (propertyData, intentData) => {
  const now = new Date();
  const deadline = new Date(propertyData.deadline);
  const minutesRemaining = (deadline - now) / (1000 * 60);

  if (minutesRemaining > 0 && minutesRemaining < ANTI_SNIPING_THRESHOLD) {
    let newDeadline = new Date(now.getTime() + ANTI_SNIPING_EXTENSION * 60 * 1000);
    
    // Solo guardar original_deadline la primera vez que se extiende
    const originalDeadline = propertyData.original_deadline ? null : propertyData.deadline;
    
    // Verificar límite máximo de extensión
    if (propertyData.original_deadline) {
      const originalDeadlineDate = new Date(propertyData.original_deadline);
      const maxAllowedDeadline = new Date(originalDeadlineDate.getTime() + ANTI_SNIPING_MAX_EXTENSION * 60 * 1000);
      
      if (newDeadline >= maxAllowedDeadline) {
        console.log('⏰ Anti-sniping: Límite máximo alcanzado, no se extiende más');
        return;
      }
    }
    
    try {
      await extendDeadlineAntiSniping(
        intentData.property_id,
        newDeadline.toISOString(),
        originalDeadline
      );
      console.log(`⏰ Anti-sniping activado: ${minutesRemaining.toFixed(1)}min restantes → extendido ${ANTI_SNIPING_EXTENSION}min`);

        // Guardar para mostrar en UI
        setAntiSnipingTriggered(true);
        setNewDeadlineTime(newDeadline.toLocaleTimeString(i18n.language || 'es', { hour: '2-digit', minute: '2-digit' }));

        // Notificar a todos los invitados (solo si es la PRIMERA extensión)
        if (!propertyData.original_deadline) {
          await sendAntiSnipingNotifications(propertyData, intentData);
        } else {
          console.log('⏭️ Anti-sniping: extensión adicional, no se envía email');
        }
      } catch (antiSnipingError) {
        // No fallar la puja si el anti-sniping falla
        console.error('Error en anti-sniping (no crítico):', antiSnipingError);
      }
    }
  };

  /**
   * Envía notificaciones de anti-sniping a todos los invitados
   */
  const sendAntiSnipingNotifications = async (propertyData, intentData) => {
    try {
      const allInvites = await getPropertyInvites(intentData.property_id);
      const invitesForEmail = allInvites.map(inv => ({
        email: inv.email,
        name: inv.name,
        token: inv.token
      }));

      await fetch('/api/send-anti-sniping-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyTitle: propertyData.title,
          propertyId: intentData.property_id,
          extensionMinutes: ANTI_SNIPING_EXTENSION,
          invites: invitesForEmail,
          baseUrl: window.location.origin
        })
      });
      console.log(`📧 Notificación anti-sniping enviada a ${invitesForEmail.length} invitados (primera extensión)`);
    } catch (notifyError) {
      console.error('Error enviando notificaciones anti-sniping (no crítico):', notifyError);
    }
  };


  /**
   * Envía notificaciones por email cuando se crea una oferta
   */
  const sendBidNotifications = async (previousTopBidder, propertyData, intentData) => {
    try {
      const propertyInvites = await getPropertyInvites(intentData.property_id);

      // CASO 1: Hay alguien superado (y no es el mismo usuario)
      if (previousTopBidder && previousTopBidder.buyerEmail !== intentData.buyer_email) {
        const previousInvite = propertyInvites.find(
          i => i.email === previousTopBidder.buyerEmail
        );

        await fetch('/api/send-bid-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            previousBidderEmail: previousTopBidder.buyerEmail,
            previousBidderName: previousTopBidder.buyerName,
            propertyTitle: propertyData.title,
            propertyId: intentData.property_id,
            inviteToken: previousInvite?.token || '',
            newBidAmount: intentData.amount,
            newBidderName: intentData.buyer_name,
            adminEmails: propertyData.adminEmails || [],
            baseUrl: window.location.origin
          })
        });
      } 
      // CASO 2: Primera oferta - solo notificar a admins
      else if (propertyData.adminEmails && propertyData.adminEmails.length > 0) {
        await fetch('/api/send-bid-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            previousBidderEmail: null,
            previousBidderName: null,
            propertyTitle: propertyData.title,
            propertyId: intentData.property_id,
            inviteToken: null,
            newBidAmount: intentData.amount,
            newBidderName: intentData.buyer_name,
            adminEmails: propertyData.adminEmails,
            baseUrl: window.location.origin
          })
        });
      }
    } catch (error) {
      console.error('Error enviando notificaciones:', error);
      // No fallar la creación de la oferta por un error en notificaciones
    }
  };

  /**
   * Tracking: Intent expirado o ya usado
   */
  const trackExpiredIntent = async () => {
    if (!inviteToken) return;

    try {
      await createEngagementEvent(
        null,
        inviteToken,
        generateSessionId(),
        'bid_intent_expired',
        { token, reason: 'expired_or_used' },
        getBrowserMetadata()
      );
    } catch (trackingError) {
      console.debug('Error tracking bid_intent_expired:', trackingError);
    }
  };

  /**
   * Tracking: Oferta verificada exitosamente
   */
  const trackVerifiedBid = async (intentData, isLastChance) => {
    try {
      await createEngagementEvent(
        intentData.property_id,
        inviteToken,
        generateSessionId(),
        'bid_verified',
        {
          amount: intentData.amount,
          propertyId: intentData.property_id,
          isLastChance
        },
        getBrowserMetadata()
      );
    } catch (trackingError) {
      console.debug('Error tracking bid_verified:', trackingError);
    }
  };

  /**
   * Helper: Obtener invite token del localStorage
   */
  const getInviteTokenFromStorage = (email, propertyId) => {
    try {
      const stored = localStorage.getItem('realtybid_invites');
      if (stored) {
        const invites = JSON.parse(stored);
        const invite = invites.find(i => 
          i.email === email && i.propertyId === propertyId
        );
        return invite?.token || null;
      }
    } catch (error) {
      console.error('Error obteniendo invite token:', error);
    }
    return null;
  };

  /**
   * Formatea cantidad en formato EUR
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat(i18n.language || 'es', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  /**
   * Navegar de vuelta a la propiedad
   */
  const handleBackToProperty = () => {
    if (intent) {
      const storedToken = getInviteTokenFromStorage(intent.buyer_email, intent.property_id);
      if (storedToken) {
        navigate(`/property/${intent.property_id}?token=${storedToken}`);
      } else {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };

  /**
   * Navegar a la propiedad con el inviteToken de la URL
   */
  const navigateToProperty = () => {
    if (intent && inviteToken) {
      navigate(`/property/${intent.property_id}?token=${inviteToken}`);
    }
  };

  return {
    // Estados
    loading,
    status,
    intent,
    property,
    message,
    antiSnipingTriggered,
    newDeadlineTime,
    inviteToken,
    // Funciones
    handleBackToProperty,
    navigateToProperty,
    formatAmount,
  };
};

export default useBidVerification;